import {
    Document,
    Page,
    Text,
    View,
    StyleSheet,
} from "@react-pdf/renderer";


/* ==========================================================
CONSTANTS
========================================================== */

const INSTALLMENT_ORDER = [
    "Registration",
    "1st Installment",
    "2nd Installment",
    "3rd Installment",
    "4th Installment",
];

const STUDENT_TYPE_ORDER = [
    "old",
    "new",
    "scholarship",
    "ward",
];

const STUDENT_TYPE_LABELS = {
    old: "Old Students",
    new: "New Students",
    scholarship: "Scholarship Students",
    ward: "Ward Students",
};

const DEFAULT_SCHOOL_NAME =
    "Jana L. Williams Educational Foundation";

const COMPANY_NAME =
    "Design by Polu Integrated School Management System";


/* ==========================================================
NUMBER FORMATTERS
========================================================== */

const money = (value) => {

    const parsed =
        Number(value || 0);

    return new Intl.NumberFormat(
        "en-US",
        {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }
    ).format(
        Number.isFinite(parsed)
            ? parsed
            : 0
    );
};


const number = (value) => {

    const parsed =
        Number(value || 0);

    return new Intl.NumberFormat(
        "en-US"
    ).format(
        Number.isFinite(parsed)
            ? parsed
            : 0
    );
};


const percent = (
    collected,
    total
) => {

    const totalValue =
        Number(total || 0);

    if (
        totalValue <= 0
    ) {
        return "0.0%";
    }

    const collectedValue =
        Number(collected || 0);

    return (
        (
            collectedValue /
            totalValue
        ) *
        100
    ).toFixed(1) + "%";
};


/* ==========================================================
COLOR PALETTE
========================================================== */

const COLORS = {

    page:
        "#FFFFFF",

    white:
        "#FFFFFF",

    black:
        "#111827",

    text:
        "#1F2937",

    muted:
        "#64748B",

    softText:
        "#94A3B8",

    border:
        "#E2E8F0",

    borderLight:
        "#EEF2F7",

    headerLight:
        "#F3F4F6",

    headerLightAlt:
        "#F8FAFC",

    tableHeader:
        "#263238",

    tableHeaderText:
        "#FFFFFF",

    rowEven:
        "#FFFFFF",

    rowOdd:
        "#F8FAFC",

    blue:
        "#DBEAFE",

    blueStrong:
        "#2563EB",

    green:
        "#DCFCE7",

    greenStrong:
        "#16A34A",

    amber:
        "#FEF3C7",

    amberStrong:
        "#D97706",

    purple:
        "#EDE9FE",

    purpleStrong:
        "#7C3AED",

    cyan:
        "#CFFAFE",

    cyanStrong:
        "#0891B2",

    rose:
        "#FFE4E6",

    roseStrong:
        "#E11D48",

    slate:
        "#F1F5F9",

};


/* ==========================================================
GLOBAL STYLES
========================================================== */

const styles =
    StyleSheet.create({

        /* ==================================================
        PORTRAIT PAGE
        ================================================== */

        page: {

            paddingTop:
                26,

            paddingLeft:
                28,

            paddingRight:
                28,

            paddingBottom:
                58,

            fontSize:
                9.5,

            fontFamily:
                "Helvetica",

            color:
                COLORS.text,

            backgroundColor:
                COLORS.page,

        },


        /* ==================================================
        LANDSCAPE PAGE

        IMPORTANT FIX:
        Extra bottom space keeps report content safely
        away from the fixed footer.
        ================================================== */

        landscapePage: {

            paddingTop:
                24,

            paddingLeft:
                28,

            paddingRight:
                28,

            paddingBottom:
                76,

            fontSize:
                9,

            fontFamily:
                "Helvetica",

            color:
                COLORS.text,

            backgroundColor:
                COLORS.page,

        },


        /* ==================================================
        HEADER
        ================================================== */

        header: {

            paddingTop:
                12,

            paddingBottom:
                12,

            paddingLeft:
                16,

            paddingRight:
                16,

            marginBottom:
                14,

            backgroundColor:
                COLORS.headerLight,

            borderWidth:
                1,

            borderColor:
                COLORS.border,

            borderRadius:
                7,

        },


        schoolName: {

            fontSize:
                8,

            color:
                COLORS.muted,

            marginBottom:
                4,

            textTransform:
                "uppercase",

            letterSpacing:
                0.65,

        },


        headerTitle: {

            fontSize:
                18,

            color:
                COLORS.black,

            fontWeight:
                "bold",

            marginBottom:
                4,

        },


        headerSubtitle: {

            fontSize:
                9.5,

            color:
                COLORS.muted,

        },


        /* ==================================================
        SECTION TITLE
        ================================================== */

        sectionTitle: {

            fontSize:
                12,

            fontWeight:
                "bold",

            color:
                COLORS.black,

            marginTop:
                10,

            marginBottom:
                8,

            paddingBottom:
                5,

            borderBottomWidth:
                1,

            borderBottomColor:
                COLORS.border,

        },


        sectionTitleFirst: {

            marginTop:
                2,

        },


        /* ==================================================
        KPI GRID
        ================================================== */

        kpiGrid: {

            flexDirection:
                "row",

            flexWrap:
                "wrap",

            marginLeft:
                -4,

            marginRight:
                -4,

            marginBottom:
                9,

        },


        kpi: {

            width:
                "25%",

            paddingLeft:
                4,

            paddingRight:
                4,

            marginBottom:
                8,

        },


        kpiInner: {

            minHeight:
                60,

            borderWidth:
                1,

            borderColor:
                COLORS.border,

            borderRadius:
                7,

            padding:
                10,

            backgroundColor:
                COLORS.white,

        },


        kpiBlue: {

            backgroundColor:
                "#F8FBFF",

            borderLeftWidth:
                3,

            borderLeftColor:
                COLORS.blueStrong,

        },


        kpiGreen: {

            backgroundColor:
                "#F8FFF9",

            borderLeftWidth:
                3,

            borderLeftColor:
                COLORS.greenStrong,

        },


        kpiAmber: {

            backgroundColor:
                "#FFFDF7",

            borderLeftWidth:
                3,

            borderLeftColor:
                COLORS.amberStrong,

        },


        kpiPurple: {

            backgroundColor:
                "#FBF9FF",

            borderLeftWidth:
                3,

            borderLeftColor:
                COLORS.purpleStrong,

        },


        kpiLabel: {

            fontSize:
                7.5,

            color:
                COLORS.muted,

            marginBottom:
                5,

            textTransform:
                "uppercase",

            letterSpacing:
                0.3,

        },


        kpiValue: {

            fontSize:
                13,

            fontWeight:
                "bold",

            color:
                COLORS.black,

        },


        /* ==================================================
        NOTE
        ================================================== */

        note: {

            padding:
                10,

            backgroundColor:
                COLORS.headerLightAlt,

            borderWidth:
                1,

            borderColor:
                COLORS.border,

            borderRadius:
                6,

            marginTop:
                3,

            marginBottom:
                9,

        },


        noteText: {

            fontSize:
                8.5,

            color:
                COLORS.muted,

            lineHeight:
                1.45,

        },


        /* ==================================================
        TABLE
        ================================================== */

        table: {

            width:
                "100%",

            borderWidth:
                1,

            borderColor:
                COLORS.border,

            marginBottom:
                11,

            backgroundColor:
                COLORS.white,

        },


        /* ==================================================
        TABLE HEADER

        Dark color is restricted to this narrow area.
        ================================================== */

        tableHeader: {

            flexDirection:
                "row",

            backgroundColor:
                COLORS.tableHeader,

            minHeight:
                29,

            alignItems:
                "center",

        },


        tableHeaderFixed: {

            flexDirection:
                "row",

            backgroundColor:
                COLORS.tableHeader,

            minHeight:
                29,

            alignItems:
                "center",

        },


        /* ==================================================
        TABLE ROWS
        ================================================== */

        row: {

            flexDirection:
                "row",

            minHeight:
                26,

            alignItems:
                "center",

            borderBottomWidth:
                1,

            borderBottomColor:
                COLORS.borderLight,

            backgroundColor:
                COLORS.rowEven,

        },


        rowAlt: {

            flexDirection:
                "row",

            minHeight:
                26,

            alignItems:
                "center",

            borderBottomWidth:
                1,

            borderBottomColor:
                COLORS.borderLight,

            backgroundColor:
                COLORS.rowOdd,

        },


        rowLast: {

            flexDirection:
                "row",

            minHeight:
                26,

            alignItems:
                "center",

            backgroundColor:
                COLORS.rowEven,

        },


        rowLastAlt: {

            flexDirection:
                "row",

            minHeight:
                26,

            alignItems:
                "center",

            backgroundColor:
                COLORS.rowOdd,

        },


        cell: {

            paddingTop:
                6,

            paddingBottom:
                6,

            paddingLeft:
                6,

            paddingRight:
                6,

            fontSize:
                8.2,

            color:
                COLORS.text,

        },


        headerCell: {

            paddingTop:
                7,

            paddingBottom:
                7,

            paddingLeft:
                6,

            paddingRight:
                6,

            fontSize:
                8,

            color:
                COLORS.tableHeaderText,

            fontWeight:
                "bold",

        },


        /* ==================================================
        TABLE WIDTHS
        ================================================== */

        gradeName: {

            width:
                "25%",

        },


        students: {

            width:
                "12%",

            textAlign:
                "center",

        },


        amount: {

            width:
                "21%",

            textAlign:
                "right",

        },


        rate: {

            width:
                "14%",

            textAlign:
                "right",

        },


        /* ==================================================
        TYPE BLOCK
        ================================================== */

        typeBlock: {

            marginBottom:
                12,

            borderWidth:
                1,

            borderColor:
                COLORS.border,

            borderRadius:
                7,

            padding:
                9,

            backgroundColor:
                COLORS.white,

        },


        typeTitle: {

            fontSize:
                11,

            fontWeight:
                "bold",

            marginBottom:
                7,

            color:
                COLORS.black,

        },


        /* ==================================================
        GRADE DETAIL BLOCK

        WHITE ONLY.

        This is the important visual fix.
        ================================================== */

        detailBlock: {

            width:
                "100%",

            marginBottom:
                10,

            padding:
                10,

            borderWidth:
                1,

            borderColor:
                COLORS.border,

            borderRadius:
                7,

            backgroundColor:
                COLORS.white,

        },


        gradeDetailTitle: {

            fontSize:
                12,

            fontWeight:
                "bold",

            color:
                COLORS.black,

            marginBottom:
                8,

        },


        detailSectionTitle: {

            fontSize:
                10.5,

            fontWeight:
                "bold",

            color:
                COLORS.black,

            marginTop:
                8,

            marginBottom:
                7,

            paddingBottom:
                4,

            borderBottomWidth:
                1,

            borderBottomColor:
                COLORS.border,

        },


        /* ==================================================
        FOOTER

        White background intentionally separates footer
        from all report content.
        ================================================== */

        footer: {

            position:
                "absolute",

            bottom:
                17,

            left:
                28,

            right:
                28,

            height:
                24,

            borderTopWidth:
                1,

            borderTopColor:
                COLORS.border,

            paddingTop:
                6,

            paddingHorizontal:
                2,

            flexDirection:
                "row",

            justifyContent:
                "space-between",

            alignItems:
                "center",

            backgroundColor:
                COLORS.white,

        },


        footerText: {

            fontSize:
                7,

            color:
                COLORS.muted,

        },


        footerCompany: {

            fontSize:
                7,

            color:
                COLORS.softText,

            textAlign:
                "center",

        },

    });


/* ==========================================================
FOOTER
========================================================== */

const Footer = ({
    year,
    schoolName,
}) => (

    <View
        style={
            styles.footer
        }
        fixed
    >

        <Text
            style={
                styles.footerText
            }
        >
            {
                schoolName ||
                DEFAULT_SCHOOL_NAME
            }
        </Text>


        <Text
            style={
                styles.footerCompany
            }
        >
            {COMPANY_NAME}
        </Text>


        <Text
            style={
                styles.footerText
            }
        >
            Academic Year: {year}
        </Text>


        <Text
            style={
                styles.footerText
            }
        >
            Page{" "}

            <Text
                render={({
                    pageNumber,
                    totalPages,
                }) =>
                    `${pageNumber} / ${totalPages}`
                }
            />

        </Text>

    </View>

);


/* ==========================================================
HEADER
========================================================== */

const Header = ({
    title,
    subtitle,
    schoolName,
}) => (

    <View
        style={
            styles.header
        }
    >

        <Text
            style={
                styles.schoolName
            }
        >
            {
                schoolName ||
                DEFAULT_SCHOOL_NAME
            }
        </Text>


        <Text
            style={
                styles.headerTitle
            }
        >
            {title}
        </Text>


        <Text
            style={
                styles.headerSubtitle
            }
        >
            {subtitle}
        </Text>

    </View>

);


/* ==========================================================
TABLE HEADER
========================================================== */

const TableHeader = ({
    children,
}) => (

    <View
        style={
            styles.tableHeaderFixed
        }
        fixed
    >
        {children}
    </View>

);


/* ==========================================================
EXECUTIVE FINANCIAL REPORT
========================================================== */

export const ExecutiveFinancialReportPDF = ({
    report,
    selectedYear,
    schoolName,
}) => {

    const summary =
        report?.summary || {};

    const year =
        selectedYear?.name ||
        report?.academic_year?.name ||
        "Academic Year";


    return (

        <Document>

            <Page
                size="A4"
                style={
                    styles.page
                }
            >

                <Header
                    schoolName={
                        schoolName
                    }
                    title="School Financial Report"
                    subtitle={
                        `Executive Financial Summary — ${year}`
                    }
                />


                <Text
                    style={[
                        styles.sectionTitle,
                        styles.sectionTitleFirst,
                    ]}
                >
                    Executive Financial Position
                </Text>


                <View
                    style={
                        styles.kpiGrid
                    }
                >

                    <View
                        style={
                            styles.kpi
                        }
                    >

                        <View
                            style={[
                                styles.kpiInner,
                                styles.kpiBlue,
                            ]}
                        >

                            <Text
                                style={
                                    styles.kpiLabel
                                }
                            >
                                TOTAL STUDENTS
                            </Text>

                            <Text
                                style={
                                    styles.kpiValue
                                }
                            >
                                {number(
                                    summary.total_students
                                )}
                            </Text>

                        </View>

                    </View>


                    <View
                        style={
                            styles.kpi
                        }
                    >

                        <View
                            style={[
                                styles.kpiInner,
                                styles.kpiPurple,
                            ]}
                        >

                            <Text
                                style={
                                    styles.kpiLabel
                                }
                            >
                                TOTAL FEES
                            </Text>

                            <Text
                                style={
                                    styles.kpiValue
                                }
                            >
                                {money(
                                    summary.total_fee
                                )}
                            </Text>

                        </View>

                    </View>


                    <View
                        style={
                            styles.kpi
                        }
                    >

                        <View
                            style={[
                                styles.kpiInner,
                                styles.kpiGreen,
                            ]}
                        >

                            <Text
                                style={
                                    styles.kpiLabel
                                }
                            >
                                TOTAL COLLECTED
                            </Text>

                            <Text
                                style={
                                    styles.kpiValue
                                }
                            >
                                {money(
                                    summary.total_collected
                                )}
                            </Text>

                        </View>

                    </View>


                    <View
                        style={
                            styles.kpi
                        }
                    >

                        <View
                            style={[
                                styles.kpiInner,
                                styles.kpiAmber,
                            ]}
                        >

                            <Text
                                style={
                                    styles.kpiLabel
                                }
                            >
                                OUTSTANDING BALANCE
                            </Text>

                            <Text
                                style={
                                    styles.kpiValue
                                }
                            >
                                {money(
                                    summary.outstanding_balance
                                )}
                            </Text>

                        </View>

                    </View>

                </View>


                <View
                    style={
                        styles.note
                    }
                >

                    <Text
                        style={
                            styles.noteText
                        }
                    >
                        Collection Rate:{" "}
                        {percent(
                            summary.total_collected,
                            summary.total_fee
                        )}

                        {"\n"}

                        This report is based on the selected
                        academic year and the fee configuration
                        and payment records returned by the
                        school financial reporting data.
                    </Text>

                </View>


                <Text
                    style={
                        styles.sectionTitle
                    }
                >
                    Installment Financial Performance
                </Text>


                <View
                    style={
                        styles.table
                    }
                >

                    <TableHeader>

                        <Text
                            style={[
                                styles.headerCell,
                                styles.gradeName,
                            ]}
                        >
                            INSTALLMENT
                        </Text>

                        <Text
                            style={[
                                styles.headerCell,
                                styles.amount,
                            ]}
                        >
                            TOTAL FEE
                        </Text>

                        <Text
                            style={[
                                styles.headerCell,
                                styles.amount,
                            ]}
                        >
                            COLLECTED
                        </Text>

                        <Text
                            style={[
                                styles.headerCell,
                                styles.amount,
                            ]}
                        >
                            OUTSTANDING
                        </Text>

                    </TableHeader>


                    {
                        INSTALLMENT_ORDER.map(
                            (
                                installment,
                                index
                            ) => {

                                const item =
                                    report
                                        ?.installment_breakdown
                                        ?.[
                                            installment
                                        ] || {};


                                const isLast =
                                    index ===
                                    INSTALLMENT_ORDER.length - 1;


                                return (

                                    <View
                                        key={
                                            installment
                                        }
                                        style={
                                            isLast
                                                ? styles.rowLast
                                                : index % 2
                                                    ? styles.rowAlt
                                                    : styles.row
                                        }
                                    >

                                        <Text
                                            style={[
                                                styles.cell,
                                                styles.gradeName,
                                            ]}
                                        >
                                            {installment}
                                        </Text>

                                        <Text
                                            style={[
                                                styles.cell,
                                                styles.amount,
                                            ]}
                                        >
                                            {money(
                                                item.total_fee
                                            )}
                                        </Text>

                                        <Text
                                            style={[
                                                styles.cell,
                                                styles.amount,
                                            ]}
                                        >
                                            {money(
                                                item.total_collected
                                            )}
                                        </Text>

                                        <Text
                                            style={[
                                                styles.cell,
                                                styles.amount,
                                            ]}
                                        >
                                            {money(
                                                item.outstanding_balance
                                            )}
                                        </Text>

                                    </View>

                                );

                            }
                        )
                    }

                </View>


                <Text
                    style={
                        styles.sectionTitle
                    }
                >
                    Student Type Financial Performance
                </Text>


                <View
                    style={
                        styles.table
                    }
                >

                    <TableHeader>

                        <Text
                            style={[
                                styles.headerCell,
                                styles.gradeName,
                            ]}
                        >
                            STUDENT TYPE
                        </Text>

                        <Text
                            style={[
                                styles.headerCell,
                                styles.students,
                            ]}
                        >
                            STUDENTS
                        </Text>

                        <Text
                            style={[
                                styles.headerCell,
                                styles.amount,
                            ]}
                        >
                            TOTAL FEE
                        </Text>

                        <Text
                            style={[
                                styles.headerCell,
                                styles.amount,
                            ]}
                        >
                            COLLECTED
                        </Text>

                        <Text
                            style={[
                                styles.headerCell,
                                styles.amount,
                            ]}
                        >
                            OUTSTANDING
                        </Text>

                    </TableHeader>


                    {
                        STUDENT_TYPE_ORDER.map(
                            (
                                type,
                                index
                            ) => {

                                const item =
                                    report
                                        ?.student_type_breakdown
                                        ?.[
                                            type
                                        ] || {};


                                const isLast =
                                    index ===
                                    STUDENT_TYPE_ORDER.length - 1;


                                return (

                                    <View
                                        key={
                                            type
                                        }
                                        style={
                                            isLast
                                                ? styles.rowLast
                                                : index % 2
                                                    ? styles.rowAlt
                                                    : styles.row
                                        }
                                    >

                                        <Text
                                            style={[
                                                styles.cell,
                                                styles.gradeName,
                                            ]}
                                        >
                                            {
                                                STUDENT_TYPE_LABELS[
                                                    type
                                                ]
                                            }
                                        </Text>

                                        <Text
                                            style={[
                                                styles.cell,
                                                styles.students,
                                            ]}
                                        >
                                            {number(
                                                item.student_count
                                            )}
                                        </Text>

                                        <Text
                                            style={[
                                                styles.cell,
                                                styles.amount,
                                            ]}
                                        >
                                            {money(
                                                item.total_fee
                                            )}
                                        </Text>

                                        <Text
                                            style={[
                                                styles.cell,
                                                styles.amount,
                                            ]}
                                        >
                                            {money(
                                                item.total_collected
                                            )}
                                        </Text>

                                        <Text
                                            style={[
                                                styles.cell,
                                                styles.amount,
                                            ]}
                                        >
                                            {money(
                                                item.outstanding_balance
                                            )}
                                        </Text>

                                    </View>

                                );

                            }
                        )
                    }

                </View>


                <Footer
                    schoolName={
                        schoolName
                    }
                    year={
                        year
                    }
                />

            </Page>

        </Document>

    );

};


/* ==========================================================
GRADE FINANCIAL REPORT
LANDSCAPE

FIXED VERSION:
- Summary page is separate.
- Every grade detail gets its own page.
- No large dark background.
- Footer is isolated.
========================================================== */

export const GradeFinancialReportPDF = ({
    report,
    gradeData,
    selectedYear,
    schoolName,
}) => {

    const year =
        selectedYear?.name ||
        report?.academic_year?.name ||
        "Academic Year";


    const grades =
        Array.isArray(gradeData)
            ? gradeData
            : Array.isArray(
                report?.grade_class_breakdown
            )
                ? report.grade_class_breakdown
                : [];


    /* ======================================================
       EMPTY REPORT
    ====================================================== */

    if (
        grades.length === 0
    ) {

        return (

            <Document>

                <Page
                    size="A4"
                    orientation="landscape"
                    style={
                        styles.landscapePage
                    }
                >

                    <Header
                        schoolName={
                            schoolName
                        }
                        title="Grade-Class Financial Report"
                        subtitle={
                            `Detailed Grade/Class Financial Analysis — ${year}`
                        }
                    />


                    <View
                        style={
                            styles.typeBlock
                        }
                    >

                        <Text
                            style={
                                styles.typeTitle
                            }
                        >
                            No Grade-Class Data
                        </Text>


                        <Text
                            style={
                                styles.noteText
                            }
                        >
                            No grade-class financial records
                            are available for the selected
                            academic year.
                        </Text>

                    </View>


                    <Footer
                        schoolName={
                            schoolName
                        }
                        year={
                            year
                        }
                    />

                </Page>

            </Document>

        );
    }


    return (

        <Document>

            {/* ==================================================
                PAGE 1
                GRADE-CLASS SUMMARY
            ================================================== */}

            <Page
                size="A4"
                orientation="landscape"
                style={
                    styles.landscapePage
                }
            >

                <Header
                    schoolName={
                        schoolName
                    }
                    title="Grade-Class Financial Report"
                    subtitle={
                        `Grade/Class Financial Analysis — ${year}`
                    }
                />


                <Text
                    style={[
                        styles.sectionTitle,
                        styles.sectionTitleFirst,
                    ]}
                >
                    Grade-Class Summary
                </Text>


                <View
                    style={
                        styles.table
                    }
                >

                    <TableHeader>

                        <Text
                            style={[
                                styles.headerCell,
                                styles.gradeName,
                            ]}
                        >
                            GRADE CLASS
                        </Text>

                        <Text
                            style={[
                                styles.headerCell,
                                styles.students,
                            ]}
                        >
                            STUDENTS
                        </Text>

                        <Text
                            style={[
                                styles.headerCell,
                                styles.amount,
                            ]}
                        >
                            TOTAL FEE
                        </Text>

                        <Text
                            style={[
                                styles.headerCell,
                                styles.amount,
                            ]}
                        >
                            COLLECTED
                        </Text>

                        <Text
                            style={[
                                styles.headerCell,
                                styles.amount,
                            ]}
                        >
                            OUTSTANDING
                        </Text>

                        <Text
                            style={[
                                styles.headerCell,
                                styles.rate,
                            ]}
                        >
                            RATE
                        </Text>

                    </TableHeader>


                    {
                        grades.map(
                            (
                                grade,
                                index
                            ) => {

                                const isLast =
                                    index ===
                                    grades.length - 1;


                                return (

                                    <View
                                        key={
                                            grade.grade_class_id ||
                                            `grade-${index}`
                                        }
                                        style={
                                            isLast
                                                ? styles.rowLast
                                                : index % 2
                                                    ? styles.rowAlt
                                                    : styles.row
                                        }
                                    >

                                        <Text
                                            style={[
                                                styles.cell,
                                                styles.gradeName,
                                            ]}
                                        >
                                            {
                                                grade.grade_class
                                            }
                                        </Text>


                                        <Text
                                            style={[
                                                styles.cell,
                                                styles.students,
                                            ]}
                                        >
                                            {number(
                                                grade.total_students
                                            )}
                                        </Text>


                                        <Text
                                            style={[
                                                styles.cell,
                                                styles.amount,
                                            ]}
                                        >
                                            {money(
                                                grade.total_fee
                                            )}
                                        </Text>


                                        <Text
                                            style={[
                                                styles.cell,
                                                styles.amount,
                                            ]}
                                        >
                                            {money(
                                                grade.total_collected
                                            )}
                                        </Text>


                                        <Text
                                            style={[
                                                styles.cell,
                                                styles.amount,
                                            ]}
                                        >
                                            {money(
                                                grade.outstanding_balance
                                            )}
                                        </Text>


                                        <Text
                                            style={[
                                                styles.cell,
                                                styles.rate,
                                            ]}
                                        >
                                            {percent(
                                                grade.total_collected,
                                                grade.total_fee
                                            )}
                                        </Text>

                                    </View>

                                );

                            }
                        )
                    }

                </View>


                <Footer
                    schoolName={
                        schoolName
                    }
                    year={
                        year
                    }
                />

            </Page>


            {/* ==================================================
                ONE PAGE PER GRADE

                THIS IS THE MAIN FIX.

                Instead of:

                    one Page
                      ├── Grade 1
                      ├── Grade 2
                      ├── Grade 3
                      └── ...

                we now generate:

                    Page → Grade 1
                    Page → Grade 2
                    Page → Grade 3

                This prevents content from fighting with the
                fixed footer.
            ================================================== */}

            {
                grades.map(
                    (
                        grade,
                        gradeIndex
                    ) => {

                        const gradeName =
                            grade.grade_class ||
                            "Grade/Class";


                        return (

                            <Page
                                key={
                                    `grade-detail-page-${grade.grade_class_id || gradeIndex}`
                                }
                                size="A4"
                                orientation="landscape"
                                style={
                                    styles.landscapePage
                                }
                            >

                                <Header
                                    schoolName={
                                        schoolName
                                    }
                                    title="Grade-Class Financial Report"
                                    subtitle={
                                        `${gradeName} — Detailed Financial Analysis — ${year}`
                                    }
                                />


                                {/* ==========================================
                                    GRADE TITLE
                                ========================================== */}

                                <View
                                    style={
                                        styles.detailBlock
                                    }
                                >

                                    <Text
                                        style={
                                            styles.gradeDetailTitle
                                        }
                                    >
                                        {gradeName}
                                    </Text>


                                    {/* ======================================
                                        INSTALLMENT BREAKDOWN
                                    ====================================== */}

                                    <Text
                                        style={
                                            styles.detailSectionTitle
                                        }
                                    >
                                        Installment Breakdown
                                    </Text>


                                    <View
                                        style={
                                            styles.table
                                        }
                                    >

                                        <TableHeader>

                                            <Text
                                                style={[
                                                    styles.headerCell,
                                                    styles.gradeName,
                                                ]}
                                            >
                                                INSTALLMENT
                                            </Text>


                                            <Text
                                                style={[
                                                    styles.headerCell,
                                                    styles.amount,
                                                ]}
                                            >
                                                TOTAL FEE
                                            </Text>


                                            <Text
                                                style={[
                                                    styles.headerCell,
                                                    styles.amount,
                                                ]}
                                            >
                                                COLLECTED
                                            </Text>


                                            <Text
                                                style={[
                                                    styles.headerCell,
                                                    styles.amount,
                                                ]}
                                            >
                                                OUTSTANDING
                                            </Text>

                                        </TableHeader>


                                        {
                                            INSTALLMENT_ORDER.map(
                                                (
                                                    installment,
                                                    index
                                                ) => {

                                                    const item =
                                                        grade
                                                            .installment_breakdown
                                                            ?.[
                                                                installment
                                                            ] || {};


                                                    const isLast =
                                                        index ===
                                                        INSTALLMENT_ORDER.length - 1;


                                                    return (

                                                        <View
                                                            key={
                                                                installment
                                                            }
                                                            style={
                                                                isLast
                                                                    ? styles.rowLast
                                                                    : index % 2
                                                                        ? styles.rowAlt
                                                                        : styles.row
                                                            }
                                                        >

                                                            <Text
                                                                style={[
                                                                    styles.cell,
                                                                    styles.gradeName,
                                                                ]}
                                                            >
                                                                {installment}
                                                            </Text>


                                                            <Text
                                                                style={[
                                                                    styles.cell,
                                                                    styles.amount,
                                                                ]}
                                                            >
                                                                {money(
                                                                    item.total_fee
                                                                )}
                                                            </Text>


                                                            <Text
                                                                style={[
                                                                    styles.cell,
                                                                    styles.amount,
                                                                ]}
                                                            >
                                                                {money(
                                                                    item.total_collected
                                                                )}
                                                            </Text>


                                                            <Text
                                                                style={[
                                                                    styles.cell,
                                                                    styles.amount,
                                                                ]}
                                                            >
                                                                {money(
                                                                    item.outstanding_balance
                                                                )}
                                                            </Text>

                                                        </View>

                                                    );

                                                }
                                            )
                                        }

                                    </View>


                                    {/* ======================================
                                        STUDENT TYPE BREAKDOWN
                                    ====================================== */}

                                    <Text
                                        style={
                                            styles.detailSectionTitle
                                        }
                                    >
                                        Student Type Breakdown
                                    </Text>


                                    <View
                                        style={
                                            styles.table
                                        }
                                    >

                                        <TableHeader>

                                            <Text
                                                style={[
                                                    styles.headerCell,
                                                    styles.gradeName,
                                                ]}
                                            >
                                                STUDENT TYPE
                                            </Text>


                                            <Text
                                                style={[
                                                    styles.headerCell,
                                                    styles.students,
                                                ]}
                                            >
                                                STUDENTS
                                            </Text>


                                            <Text
                                                style={[
                                                    styles.headerCell,
                                                    styles.amount,
                                                ]}
                                            >
                                                TOTAL FEE
                                            </Text>


                                            <Text
                                                style={[
                                                    styles.headerCell,
                                                    styles.amount,
                                                ]}
                                            >
                                                COLLECTED
                                            </Text>


                                            <Text
                                                style={[
                                                    styles.headerCell,
                                                    styles.amount,
                                                ]}
                                            >
                                                OUTSTANDING
                                            </Text>

                                        </TableHeader>


                                        {
                                            STUDENT_TYPE_ORDER.map(
                                                (
                                                    type,
                                                    index
                                                ) => {

                                                    const item =
                                                        grade
                                                            .student_type_breakdown
                                                            ?.[
                                                                type
                                                            ] || {};


                                                    const isLast =
                                                        index ===
                                                        STUDENT_TYPE_ORDER.length - 1;


                                                    return (

                                                        <View
                                                            key={
                                                                type
                                                            }
                                                            style={
                                                                isLast
                                                                    ? styles.rowLast
                                                                    : index % 2
                                                                        ? styles.rowAlt
                                                                        : styles.row
                                                            }
                                                        >

                                                            <Text
                                                                style={[
                                                                    styles.cell,
                                                                    styles.gradeName,
                                                                ]}
                                                            >
                                                                {
                                                                    STUDENT_TYPE_LABELS[
                                                                        type
                                                                    ]
                                                                }
                                                            </Text>


                                                            <Text
                                                                style={[
                                                                    styles.cell,
                                                                    styles.students,
                                                                ]}
                                                            >
                                                                {number(
                                                                    item.student_count
                                                                )}
                                                            </Text>


                                                            <Text
                                                                style={[
                                                                    styles.cell,
                                                                    styles.amount,
                                                                ]}
                                                            >
                                                                {money(
                                                                    item.total_fee
                                                                )}
                                                            </Text>


                                                            <Text
                                                                style={[
                                                                    styles.cell,
                                                                    styles.amount,
                                                                ]}
                                                            >
                                                                {money(
                                                                    item.total_collected
                                                                )}
                                                            </Text>


                                                            <Text
                                                                style={[
                                                                    styles.cell,
                                                                    styles.amount,
                                                                ]}
                                                            >
                                                                {money(
                                                                    item.outstanding_balance
                                                                )}
                                                            </Text>

                                                        </View>

                                                    );

                                                }
                                            )
                                        }

                                    </View>

                                </View>


                                <Footer
                                    schoolName={
                                        schoolName
                                    }
                                    year={
                                        year
                                    }
                                />

                            </Page>

                        );

                    }
                )
            }

        </Document>

    );

};


/* ==========================================================
STUDENT TYPE FINANCIAL REPORT
LANDSCAPE
========================================================== */

export const StudentTypeFinancialReportPDF = ({
    report,
    selectedYear,
    schoolName,
}) => {

    const year =
        selectedYear?.name ||
        report?.academic_year?.name ||
        "Academic Year";


    return (

        <Document>

            <Page
                size="A4"
                orientation="landscape"
                style={
                    styles.landscapePage
                }
            >

                <Header
                    schoolName={
                        schoolName
                    }
                    title="Student Type Financial Report"
                    subtitle={
                        `Student Type & Installment Analysis — ${year}`
                    }
                />


                {
                    STUDENT_TYPE_ORDER.map(
                        (
                            type,
                            typeIndex
                        ) => {

                            const studentType =
                                report
                                    ?.student_type_breakdown
                                    ?.[
                                        type
                                    ] || {};


                            return (

                                <View
                                    key={
                                        type
                                    }
                                    style={
                                        styles.typeBlock
                                    }
                                    break={
                                        typeIndex > 0
                                    }
                                >

                                    <Text
                                        style={
                                            styles.typeTitle
                                        }
                                    >
                                        {
                                            STUDENT_TYPE_LABELS[
                                                type
                                            ]
                                        }
                                    </Text>


                                    <View
                                        style={
                                            styles.table
                                        }
                                    >

                                        <TableHeader>

                                            <Text
                                                style={[
                                                    styles.headerCell,
                                                    styles.gradeName,
                                                ]}
                                            >
                                                SUMMARY
                                            </Text>


                                            <Text
                                                style={[
                                                    styles.headerCell,
                                                    styles.students,
                                                ]}
                                            >
                                                STUDENTS
                                            </Text>


                                            <Text
                                                style={[
                                                    styles.headerCell,
                                                    styles.amount,
                                                ]}
                                            >
                                                TOTAL FEE
                                            </Text>


                                            <Text
                                                style={[
                                                    styles.headerCell,
                                                    styles.amount,
                                                ]}
                                            >
                                                COLLECTED
                                            </Text>


                                            <Text
                                                style={[
                                                    styles.headerCell,
                                                    styles.amount,
                                                ]}
                                            >
                                                OUTSTANDING
                                            </Text>


                                            <Text
                                                style={[
                                                    styles.headerCell,
                                                    styles.rate,
                                                ]}
                                            >
                                                RATE
                                            </Text>

                                        </TableHeader>


                                        <View
                                            style={
                                                styles.rowLast
                                            }
                                        >

                                            <Text
                                                style={[
                                                    styles.cell,
                                                    styles.gradeName,
                                                ]}
                                            >
                                                Overall
                                            </Text>


                                            <Text
                                                style={[
                                                    styles.cell,
                                                    styles.students,
                                                ]}
                                            >
                                                {number(
                                                    studentType.student_count
                                                )}
                                            </Text>


                                            <Text
                                                style={[
                                                    styles.cell,
                                                    styles.amount,
                                                ]}
                                            >
                                                {money(
                                                    studentType.total_fee
                                                )}
                                            </Text>


                                            <Text
                                                style={[
                                                    styles.cell,
                                                    styles.amount,
                                                ]}
                                            >
                                                {money(
                                                    studentType.total_collected
                                                )}
                                            </Text>


                                            <Text
                                                style={[
                                                    styles.cell,
                                                    styles.amount,
                                                ]}
                                            >
                                                {money(
                                                    studentType.outstanding_balance
                                                )}
                                            </Text>


                                            <Text
                                                style={[
                                                    styles.cell,
                                                    styles.rate,
                                                ]}
                                            >
                                                {percent(
                                                    studentType.total_collected,
                                                    studentType.total_fee
                                                )}
                                            </Text>

                                        </View>

                                    </View>


                                    <Text
                                        style={
                                            styles.detailSectionTitle
                                        }
                                    >
                                        Installment Breakdown
                                    </Text>


                                    <View
                                        style={
                                            styles.table
                                        }
                                    >

                                        <TableHeader>

                                            <Text
                                                style={[
                                                    styles.headerCell,
                                                    styles.gradeName,
                                                ]}
                                            >
                                                INSTALLMENT
                                            </Text>


                                            <Text
                                                style={[
                                                    styles.headerCell,
                                                    styles.amount,
                                                ]}
                                            >
                                                TOTAL FEE
                                            </Text>


                                            <Text
                                                style={[
                                                    styles.headerCell,
                                                    styles.amount,
                                                ]}
                                            >
                                                COLLECTED
                                            </Text>


                                            <Text
                                                style={[
                                                    styles.headerCell,
                                                    styles.amount,
                                                ]}
                                            >
                                                OUTSTANDING
                                            </Text>

                                        </TableHeader>


                                        {
                                            INSTALLMENT_ORDER.map(
                                                (
                                                    installment,
                                                    index
                                                ) => {

                                                    const item =
                                                        studentType
                                                            .installments
                                                            ?.[
                                                                installment
                                                            ] || {};


                                                    const isLast =
                                                        index ===
                                                        INSTALLMENT_ORDER.length - 1;


                                                    return (

                                                        <View
                                                            key={
                                                                installment
                                                            }
                                                            style={
                                                                isLast
                                                                    ? styles.rowLast
                                                                    : index % 2
                                                                        ? styles.rowAlt
                                                                        : styles.row
                                                            }
                                                        >

                                                            <Text
                                                                style={[
                                                                    styles.cell,
                                                                    styles.gradeName,
                                                                ]}
                                                            >
                                                                {installment}
                                                            </Text>


                                                            <Text
                                                                style={[
                                                                    styles.cell,
                                                                    styles.amount,
                                                                ]}
                                                            >
                                                                {money(
                                                                    item.total_fee
                                                                )}
                                                            </Text>


                                                            <Text
                                                                style={[
                                                                    styles.cell,
                                                                    styles.amount,
                                                                ]}
                                                            >
                                                                {money(
                                                                    item.total_collected
                                                                )}
                                                            </Text>


                                                            <Text
                                                                style={[
                                                                    styles.cell,
                                                                    styles.amount,
                                                                ]}
                                                            >
                                                                {money(
                                                                    item.outstanding_balance
                                                                )}
                                                            </Text>

                                                        </View>

                                                    );

                                                }
                                            )
                                        }

                                    </View>

                                </View>

                            );

                        }
                    )
                }


                <Footer
                    schoolName={
                        schoolName
                    }
                    year={
                        year
                    }
                />

            </Page>

        </Document>

    );

};


export default ExecutiveFinancialReportPDF;

